import { NmLoader } from '../NmLoader';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoPersonAddOutline, IoTrashOutline } from 'react-icons/io5';
import { NmButton } from '../NmButton';
import { NmInput } from '../NmInput';
import {
  coachAthlete,
  type AssistantAssignment,
  type AssistantCoachLookup,
} from '../../services/api';

const DEFAULT_PERMISSIONS = {
  view: true,
  edit_workouts: true,
  edit_zones: true,
  edit_events: true,
  manage_athlete: false,
};

interface Props {
  athlete: { id: number; full_name?: string };
  onClose: () => void;
}

export function CoachAssistantModal({ athlete, onClose }: Props) {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState<AssistantAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [mobile, setMobile] = useState('');
  const [foundCoach, setFoundCoach] = useState<AssistantCoachLookup | null>(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await coachAthlete.getAthleteAssistantCoaches(athlete.id);
      setAssignments(res.data?.data?.assignments ?? []);
    } catch {
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [athlete.id]);

  useEffect(() => {
    setMobile('');
    setFoundCoach(null);
    setError('');
    setSuccess('');
    void loadAssignments();
  }, [athlete.id, loadAssignments]);

  async function handleSearch() {
    setError('');
    setSuccess('');
    setFoundCoach(null);
    const trimmed = mobile.trim();
    if (trimmed.length < 11) {
      setError(t('home.coach.assistant.error.invalidMobile'));
      return;
    }
    setSearching(true);
    try {
      const res = await coachAthlete.searchAssistantCoachByMobile(trimmed);
      setFoundCoach(res.data.data.coach);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('home.coach.assistant.error.notFound');
      setError(msg);
    } finally {
      setSearching(false);
    }
  }

  async function handleAdd() {
    if (!foundCoach) return;
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      await coachAthlete.addAssistantCoach({
        athlete_id: athlete.id,
        assistant_coach_id: foundCoach.id,
        permissions: DEFAULT_PERMISSIONS,
      });
      setMobile('');
      setFoundCoach(null);
      setSuccess(t('home.coach.assistant.addSuccess'));
      await loadAssignments();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('home.coach.assistant.error.addFailed');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemove(item: AssistantAssignment) {
    const name = item.coach?.full_name ?? t('home.coach.assistant.fallbackName');
    if (!window.confirm(t('home.coach.assistant.removeConfirm', { name }))) return;
    setRemovingId(item.id);
    setError('');
    try {
      await coachAthlete.removeAssistantCoach(item.id);
      await loadAssignments();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('home.coach.assistant.error.removeFailed');
      setError(msg);
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div style={s.backdrop} className="title-bar-no-drag" onClick={onClose}>
      <div className="modal-surface" style={s.panel} onClick={(e) => e.stopPropagation()}>
        <header style={s.header}>
          <div>
            <h2 style={s.title}>{t('home.coach.assistant.title')}</h2>
            <p style={s.subtitle}>{athlete.full_name}</p>
          </div>
          <button type="button" onClick={onClose} style={s.closeBtn} aria-label={t('common.close')}>
            ×
          </button>
        </header>

        <div style={s.body}>
          <p style={s.hint}>{t('home.coach.assistant.hint')}</p>

          <p style={s.label}>{t('home.coach.assistant.addByMobile')}</p>
          <div style={s.row}>
            <NmInput
              label={t('home.coach.assistant.mobileLabel')}
              value={mobile}
              onChange={(e) => {
                setMobile(e.target.value.replace(/\D/g, '').slice(0, 11));
                setFoundCoach(null);
              }}
              placeholder={t('home.coach.assistant.mobilePlaceholder')}
              inputMode="numeric"
            />
            <NmButton variant="secondary" size="sm" loading={searching} onClick={() => void handleSearch()}>
              {t('home.coach.assistant.find')}
            </NmButton>
          </div>

          {foundCoach && (
            <div style={s.foundCard}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={s.previewName}>{foundCoach.full_name}</div>
                <div style={s.previewMobile}>{foundCoach.mobile}</div>
              </div>
              <NmButton
                variant="primary"
                size="sm"
                loading={submitting}
                onClick={() => void handleAdd()}
              >
                <IoPersonAddOutline size={16} aria-hidden />
                {t('home.coach.assistant.add')}
              </NmButton>
            </div>
          )}

          <p style={{ ...s.label, marginTop: 16 }}>{t('home.coach.assistant.activeList')}</p>

          {loading ? (
            <p style={s.muted}><NmLoader size="sm" inline label={t('common.loading', 'Loading…')} /></p>
          ) : assignments.length === 0 ? (
            <p style={s.muted}>{t('home.coach.assistant.empty')}</p>
          ) : (
            <ul style={s.list}>
              {assignments.map((item) => (
                <li key={item.id} style={s.assignmentRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.previewName}>
                      {item.coach?.full_name ?? `#${item.coach_id}`}
                    </div>
                    <div style={s.previewMobile}>
                      {item.coach?.mobile ?? item.coach?.email ?? ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="lib-icon-btn lib-icon-btn--xs lib-icon-btn--danger coach-athlete-actions__btn"
                    aria-label={t('home.coach.assistant.remove')}
                    disabled={removingId === item.id}
                    onClick={() => void handleRemove(item)}
                  >
                    <IoTrashOutline size={16} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {error && <p style={s.error}>{error}</p>}
          {success && <p style={s.success}>{success}</p>}
        </div>

        <footer style={s.footer}>
          <NmButton variant="ghost" onClick={onClose}>
            {t('common.close')}
          </NmButton>
        </footer>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 4000,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: 0,
  },
  panel: {
    width: '100%',
    maxWidth: 480,
    maxHeight: 'min(92vh, 640px)',
    background: 'var(--surface-0)',
    borderRadius: 'var(--r-4) var(--r-4) 0 0',
    boxShadow: 'var(--nm-raised)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '16px 20px 8px',
    borderBottom: '1px solid var(--surface-sunken)',
  },
  title: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-display)',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: 12,
    color: 'var(--fg-4)',
  },
  closeBtn: {
    border: 'none',
    background: 'none',
    fontSize: 28,
    lineHeight: 1,
    cursor: 'pointer',
    color: 'var(--fg-4)',
    padding: 0,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 20px',
  },
  hint: {
    margin: '0 0 14px',
    fontSize: 12,
    color: 'var(--fg-3)',
    lineHeight: 1.5,
  },
  label: {
    margin: '0 0 8px',
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--fg-2)',
  },
  row: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-end',
    marginBottom: 12,
  },
  foundCard: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    padding: '12px 14px',
    borderRadius: 'var(--r-3)',
    background: 'var(--surface-sunken)',
    border: '1px solid var(--vo2-pink-300)',
    marginBottom: 8,
  },
  previewName: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--fg-1)',
  },
  previewMobile: {
    fontSize: 11,
    color: 'var(--fg-4)',
    fontFamily: 'var(--font-mono)',
    marginTop: 2,
  },
  muted: {
    margin: '8px 0',
    fontSize: 12,
    color: 'var(--fg-4)',
    textAlign: 'center',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  assignmentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 'var(--r-3)',
    background: 'var(--surface-sunken)',
    marginBottom: 8,
  },
  error: {
    margin: '10px 0 0',
    fontSize: 12,
    color: '#dc2626',
  },
  success: {
    margin: '10px 0 0',
    fontSize: 12,
    color: 'var(--vo2-green-600)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '12px 20px 16px',
    borderTop: '1px solid var(--surface-sunken)',
  },
};
